import { Card } from '@/components/ui/card'
import { useDashboard } from '../useDashboard'

interface MiniTileProps {
	label: string
	value: number | undefined
}

function MiniTile({ label, value }: MiniTileProps) {
	return (
		<div className="rounded-md bg-gray-50 p-3 text-center">
			<p className="text-2xl font-bold text-gray-900">{value ?? '—'}</p>
			<p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
		</div>
	)
}

/**
 * Numeric self-stats tiles for the dashboard. Four counters drawn from
 * the existing viewer queries — no new endpoint:
 *   - Total opened requests : `stats/my.openRequests` (every request the
 *                             caller has submitted, regardless of state)
 *   - Pending approvals     : `stats/my.pendingApprovals`
 *   - Pending execution     : `stats/my.pendingExecution` (caller-submitted
 *                             requests in the APPROVED state — past
 *                             approvals, awaiting ticket lifecycle)
 *   - Closed last 30 days   : `total` from a scoped /requests query
 *
 * Renders for every non-VIEWER role. Plain USERs got nothing numeric on
 * the dashboard previously; admins already see the Admin Snapshot below
 * this row but benefit from a self-scoped summary here too.
 */
export function MyStatsTilesWidget() {
	const { statsMyQuery, closedLast30dQuery } = useDashboard()
	const stats = statsMyQuery.data
	const closedLast30d = closedLast30dQuery.data?.total

	return (
		<Card className="p-4">
			<h2 className="mb-3 text-sm font-semibold text-gray-700">Your activity</h2>
			<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
				<MiniTile label="Total opened requests" value={stats?.openRequests} />
				<MiniTile label="Pending approvals" value={stats?.pendingApprovals} />
				<MiniTile label="Pending execution" value={stats?.pendingExecution} />
				<MiniTile label="Closed last 30 days" value={closedLast30d} />
			</div>
		</Card>
	)
}
